/**
 * The books this phone can open with no signal.
 *
 * The installed app cold-starts at the start page. That page cannot ask the
 * server who is signed in, and the scorebook links live on screens that also
 * need the server, so a saved game used to be unreachable until signal
 * returned (issue #208). The book itself already opens from this store. This
 * module is how the start page finds it.
 *
 * A game that was only marked as starting, with no cached book, is left out.
 * Opening that one still needs the server to name the game (ADR-0061).
 */

import { listCachedGames, logFor, type CachedGame } from './db.js';

export interface SavedBook {
  readonly id: string;
  readonly status: 'scheduled' | 'live' | 'final';
  /** The latest play's wall clock, when the log has one a person can read. */
  readonly latestWallClock: string | null;
}

/**
 * How long the start page waits before showing the no-connection screen.
 *
 * A stuck database must not hold that screen on "One moment" (the same bound
 * the schedule form uses for a hung preferences read). If the read finishes
 * later with a book, the screen updates.
 */
export const SAVED_GAME_WAIT_MS = 1_500;

const WHEN = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function isStatus(value: unknown): value is SavedBook['status'] {
  return value === 'scheduled' || value === 'live' || value === 'final';
}

function latestWallClock(gameId: string): Promise<string | null> {
  return logFor(gameId).then((rows) => {
    let latest: string | null = null;
    for (const row of rows) {
      const wall = row.event.wallClock;
      if (wall.length === 0 || Number.isNaN(new Date(wall).getTime())) continue;
      if (latest === null || wall > latest) latest = wall;
    }
    return latest;
  });
}

function byRecency(left: SavedBook, right: SavedBook): number {
  const leftFinal = left.status === 'final' ? 1 : 0;
  const rightFinal = right.status === 'final' ? 1 : 0;
  if (leftFinal !== rightFinal) return leftFinal - rightFinal;
  const leftWhen = left.latestWallClock ?? '';
  const rightWhen = right.latestWallClock ?? '';
  if (leftWhen !== rightWhen) {
    if (leftWhen === '') return 1;
    if (rightWhen === '') return -1;
    return rightWhen.localeCompare(leftWhen);
  }
  return left.id.localeCompare(right.id);
}

/** Books on this device, open games before finished ones, newest play first. */
export async function savedOnDevice(): Promise<readonly SavedBook[]> {
  const games = await listCachedGames();
  const books = await Promise.all(
    games
      .filter(
        (game): game is CachedGame & { status: SavedBook['status'] } =>
          typeof game.id === 'string' && game.id.length > 0 && isStatus(game.status),
      )
      .map(async (game) => ({
        id: game.id,
        status: game.status,
        // One unreadable log must not hide the other books. The link still opens.
        latestWallClock: await latestWallClock(game.id).catch(() => null),
      })),
  );
  return books.sort(byRecency);
}

/** The words on the link that opens one book. One book needs no qualifier. */
export function resumeLabel(book: SavedBook, alone: boolean): string {
  const action = book.status === 'final' ? 'Open the finished game' : 'Resume your game';
  if (alone) return action;
  return `${action}, ${whenLabel(book.latestWallClock)}`;
}

function whenLabel(iso: string | null): string {
  if (iso === null) return 'no plays yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'no plays yet';
  return WHEN.format(date);
}
